import React, {
  useEffect,
  useState,
} from 'react';

import {
  useTranslation,
} from 'react-i18next';

import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
} from 'lucide-react';

import {
  RootState,
} from '../store';

import {
  clearError,
  loginFailure,
  loginStart,
  loginSuccess,
} from '../store/slices/authSlice';

import {
  setLanguage,
} from '@digitaltattoo/redux-slices';

import {
  clientAuthApi,
} from '../services/clientAuthApi';

import {
  ClientLoginRequest,
} from '@digitaltattoo/types';

import {
  useFrontoffWorkingMode,
} from '../hooks/useSystemStatus';

interface LoginPageProps {
  /**
   * When true, renders as an inline form
   * without the full-page layout.
   */
  inline?: boolean;

  /**
   * Called after successful login in inline mode.
   */
  onSuccess?: () => void;

  /**
   * Called when user clicks back in inline mode.
   */
  onCancel?: () => void;
}

type ClientLoginError = Error & {
  code?: string;
  email?: string;
  status?: number;
};

export function LoginPage({
  inline = false,
  onSuccess,
  onCancel,
}: LoginPageProps = {}) {
  const {
    t,
    i18n,
  } = useTranslation();

  const dispatch =
    useDispatch();

  const navigate =
    useNavigate();

  const location =
    useLocation();

  const {
    isLimited,
    loading: workingModeLoading,
  } =
    useFrontoffWorkingMode();

  const {
    loading,
    error,
    isAuthenticated,
  } =
    useSelector(
      (
        state: RootState
      ) => state.auth
    );

  const [
    formData,
    setFormData,
  ] =
    useState<ClientLoginRequest>({
      email: '',
      password: '',
      rememberMe: false,
    });

  const [
    showPassword,
    setShowPassword,
  ] =
    useState(false);

  const [
    validationErrors,
    setValidationErrors,
  ] =
    useState<
      Record<string, string>
    >({});

  const [
    unverifiedEmail,
    setUnverifiedEmail,
  ] =
    useState<string | null>(
      null
    );

  const [
    resendingVerification,
    setResendingVerification,
  ] =
    useState(false);

  const [
    resendSuccess,
    setResendSuccess,
  ] =
    useState(false);

  const [
    resendError,
    setResendError,
  ] =
    useState<string | null>(
      null
    );

  useEffect(() => {
    if (
      !isAuthenticated
    ) {
      return;
    }

    if (
      inline &&
      onSuccess
    ) {
      onSuccess();
      return;
    }

    if (
      workingModeLoading
    ) {
      return;
    }

    const stateFrom =
      (
        location.state as any
      )?.from;

    const from =
      typeof stateFrom ===
      'string'
        ? stateFrom
        : stateFrom
            ?.pathname ||
          '/account';

    /*
     * In limited mode account pages are unavailable.
     */
    navigate(
      isLimited
        ? '/'
        : from,
      {
        replace: true,
      }
    );
  }, [
    isAuthenticated,
    navigate,
    location,
    inline,
    onSuccess,
    isLimited,
    workingModeLoading,
  ]);

  /*
   * Clear previous auth error when opening page.
   */
  useEffect(() => {
    dispatch(
      clearError()
    );
  }, [
    dispatch,
  ]);


  const handleInputChange = (
    field:
      keyof ClientLoginRequest,
    value:
      string | boolean
  ) => {
    setFormData(
      (
        previous
      ) => ({
        ...previous,
        [field]:
          value,
      })
    );

    /*
     * Clear validation error for edited field.
     */
    if (
      validationErrors[
        field
      ]
    ) {
      setValidationErrors(
        (
          previous
        ) => {
          const next = {
            ...previous,
          };

          delete next[
            field
          ];

          return next;
        }
      );
    }

    /*
     * If the email changes after an EMAIL_NOT_VERIFIED
     * response, reset the verification warning.
     */
    if (
      field ===
        'email' &&
      typeof value ===
        'string'
    ) {
      const current =
        value
          .trim()
          .toLowerCase();

      const blocked =
        unverifiedEmail
          ?.trim()
          .toLowerCase();

      if (
        blocked &&
        current !==
          blocked
      ) {
        setUnverifiedEmail(
          null
        );

        setResendSuccess(
          false
        );

        setResendError(
          null
        );

        dispatch(
          clearError()
        );
      }
    }
  };

  const resolveLoginError = (
    loginError:
      unknown
  ): string => {
    if (
      loginError instanceof
      Error
    ) {
      const typedError =
        loginError as ClientLoginError;

      if (
        typedError.code ===
        'INVALID_CREDENTIALS'
      ) {
        return t(
          'auth.login.errors.invalidCredentials'
        );
      }

      const message =
        loginError.message
          .toLowerCase();

      if (
        message.includes(
          'invalid email or password'
        ) ||
        message.includes(
          'invalid credentials'
        )
      ) {
        return t(
          'auth.login.errors.invalidCredentials'
        );
      }

      return (
        loginError.message ||
        t(
          'auth.login.errors.invalidCredentials'
        )
      );
    }

    return t(
      'auth.login.errors.invalidCredentials'
    );
  };

  const validateForm =
    (): boolean => {
      const errors:
        Record<
          string,
          string
        > = {};

      const email =
        formData.email
          .trim();

      if (
        !email
      ) {
        errors.email =
          t(
            'auth.login.errors.required'
          );
      } else if (
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          email
        )
      ) {
        errors.email =
          t(
            'auth.login.errors.invalidEmail'
          );
      }

      if (
        !formData.password
      ) {
        errors.password =
          t(
            'auth.login.errors.required'
          );
      }

      setValidationErrors(
        errors
      );

      return (
        Object.keys(
          errors
        ).length === 0
      );
    };

  const handleSubmit =
    async (
      event:
        React.FormEvent
    ) => {
      event.preventDefault();

      if (
        !validateForm()
      ) {
        return;
      }

      /*
       * Reset messages from the previous attempt.
       */
      setUnverifiedEmail(
        null
      );

      setResendSuccess(
        false
      );

      setResendError(
        null
      );

      dispatch(
        loginStart()
      );

      try {
        const response =
          await clientAuthApi.login(
            {
              ...formData,

              email:
                formData.email
                  .trim()
                  .toLowerCase(),
            }
          );

        if (
          response.success &&
          response.token &&
          response.refreshToken &&
          response.user
        ) {
          if (
            response.user
              .emailVerified ===
            false
          ) {
            const email =
              response.user
                .email ||
              formData.email;

            setUnverifiedEmail(
              email
            );

            dispatch(
              loginFailure(
                "Votre adresse email n'a pas encore été vérifiée. Consultez votre boîte mail et cliquez sur le lien de vérification."
              )
            );

            return;
          }

          dispatch(
            loginSuccess({
              user:
                response.user,

              token:
                response.token,

              refreshToken:
                response.refreshToken,
            })
          );

          const preferredLanguage =
            (
              response.user as any
            )
              .preferredLanguage ||
            'fr';

          if (
            preferredLanguage !==
            i18n.language
          ) {
            await i18n.changeLanguage(
              preferredLanguage
            );

            dispatch(
              setLanguage(
                preferredLanguage as
                  | 'fr'
                  | 'en'
              )
            );
          }

          /*
           * Navigation happens through useEffect.
           */
          return;
        }

        dispatch(
          loginFailure(
            resolveLoginError(
              new Error(
                response.message ||
                  'Login failed'
              )
            )
          )
        );
      } catch (
        caughtError
      ) {
        console.error(
          'Login error:',
          caughtError
        );

        const authError =
          caughtError as ClientLoginError;

        if (
          authError.code ===
          'EMAIL_NOT_VERIFIED'
        ) {
          const email =
            authError.email ||
            formData.email
              .trim()
              .toLowerCase();

          setUnverifiedEmail(
            email
          );

          setResendSuccess(
            false
          );

          setResendError(
            null
          );

          dispatch(
            loginFailure(
              authError.message ||
                "Votre adresse email n'a pas encore été vérifiée. Consultez votre boîte mail et cliquez sur le lien de vérification."
            )
          );

          return;
        }

        dispatch(
          loginFailure(
            resolveLoginError(
              caughtError
            )
          )
        );
      }
    };

  const handleResendVerification =
    async () => {
      if (
        !unverifiedEmail
      ) {
        return;
      }

      setResendingVerification(
        true
      );

      setResendSuccess(
        false
      );

      setResendError(
        null
      );

      try {
        await clientAuthApi
          .resendVerificationEmail(
            unverifiedEmail
          );

        setResendSuccess(
          true
        );
      } catch (
        caughtError
      ) {
        console.error(
          'Resend verification error:',
          caughtError
        );

        setResendError(
          caughtError instanceof
            Error
            ? caughtError.message
            : "Impossible de renvoyer l'email de vérification."
        );
      } finally {
        setResendingVerification(
          false
        );
      }
    };

  const renderForm =
    () => (
      <form
        className="space-y-6"
        onSubmit={
          handleSubmit
        }
      >
        <div className="space-y-4">
          {/* EMAIL */}

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-textPrimary mb-2"
            >
              {t(
                'auth.login.email'
              )}
            </label>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-textSecondary" />
              </div>

              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={
                  false
                }
                required
                value={
                  formData.email
                }
                onChange={(
                  event
                ) =>
                  handleInputChange(
                    'email',
                    event.target.value.toLowerCase()
                  )
                }
                className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                  validationErrors.email ||
                  (
                    error &&
                    !unverifiedEmail
                  )
                    ? 'border-red-300 bg-red-50'
                    : unverifiedEmail
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-gray-300 bg-white'
                }`}
                placeholder={
                  t(
                    'auth.login.email'
                  )
                }
              />
            </div>

            {validationErrors.email && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />

                {
                  validationErrors.email
                }
              </p>
            )}
          </div>

          {/* PASSWORD */}

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-textPrimary mb-2"
            >
              {t(
                'auth.login.password'
              )}
            </label>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-textSecondary" />
              </div>

              <input
                id="password"
                name="password"
                type={
                  showPassword
                    ? 'text'
                    : 'password'
                }
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={
                  false
                }
                required
                value={
                  formData.password
                }
                onChange={(
                  event
                ) =>
                  handleInputChange(
                    'password',
                    event.target.value
                  )
                }
                className={`block w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                  validationErrors.password ||
                  (
                    error &&
                    !unverifiedEmail
                  )
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-300 bg-white'
                }`}
                placeholder={
                  t(
                    'auth.login.password'
                  )
                }
              />

              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() =>
                  setShowPassword(
                    (
                      previous
                    ) =>
                      !previous
                  )
                }
                aria-label={
                  showPassword
                    ? 'Masquer le mot de passe'
                    : 'Afficher le mot de passe'
                }
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-textSecondary hover:text-textPrimary" />
                ) : (
                  <Eye className="h-5 w-5 text-textSecondary hover:text-textPrimary" />
                )}
              </button>
            </div>

            {validationErrors.password && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />

                {
                  validationErrors.password
                }
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="rememberMe"
              name="rememberMe"
              type="checkbox"
              checked={
                Boolean(
                  formData.rememberMe
                )
              }
              onChange={(
                event
              ) =>
                handleInputChange(
                  'rememberMe',
                  event.target.checked
                )
              }
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />

            <label
              htmlFor="rememberMe"
              className="ml-2 block text-sm text-textSecondary"
            >
              {t(
                'auth.login.rememberMe'
              )}
            </label>
          </div>

          {!inline && (
            <div className="text-sm">
              <Link
                to="/forgot-password"
                className="fo-link font-medium"
              >
                {t(
                  'auth.login.forgotPassword'
                )}
              </Link>
            </div>
          )}
        </div>

        {/*
         * =====================================================
         * NORMAL LOGIN ERROR
         * =====================================================
         */}

        {error &&
          !unverifiedEmail && (
            <div
              className="bg-red-50 border border-red-200 rounded-lg p-4"
              role="alert"
            >
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />

                <div className="ml-3">
                  <p className="text-sm text-red-800">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

        {/*
         * =====================================================
         * EMAIL NOT VERIFIED
         * =====================================================
         */}

        {unverifiedEmail && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-amber-700" />
              </div>

              <div className="flex-1 min-w-0">
                <p className="font-semibold text-amber-900">
                  Adresse email non vérifiée
                </p>

                <p className="mt-1 text-sm text-amber-800">
                  Votre compte existe, mais votre adresse email doit être vérifiée avant de pouvoir vous connecter.
                </p>

                <p className="mt-3 text-sm text-amber-800">
                  Consultez votre boîte mail :
                </p>

                <p className="font-semibold text-sm text-bold break-all">
                  {unverifiedEmail}
                </p>

                <p className="mt-2 text-xs text-amber-700">
                  Pensez également à vérifier votre dossier de courriers indésirables.
                </p>

                {/* RESEND */}

                <button
                  type="button"
                  onClick={
                    handleResendVerification
                  }
                  disabled={
                    resendingVerification
                  }
                  className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-amber-400 bg-white text-sm font-medium text-amber-900 hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendingVerification ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />

                      <span>
                        Envoi en cours...
                      </span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />

                      <span>
                        Renvoyer l'email de vérification
                      </span>
                    </>
                  )}
                </button>

                {/* SUCCESS */}

                {resendSuccess && (
                  <div
                    className="mt-4 flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3"
                    role="status"
                  >
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />

                    <div>
                      <p className="text-sm font-medium text-green-800">
                        Email envoyé
                      </p>

                      <p className="mt-0.5 text-xs text-green-700">
                        Un nouveau lien de vérification vient de vous être envoyé.
                      </p>
                    </div>
                  </div>
                )}

                {/* RESEND ERROR */}

                {resendError && (
                  <div
                    className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3"
                    role="alert"
                  >
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />

                    <p className="text-sm text-red-800">
                      {resendError}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* SUBMIT */}

        <div>
          <button
            type="submit"
            disabled={
              loading
            }
            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            {loading ? (
              <div className="flex items-center">
                <Loader2 className="animate-spin h-5 w-5 mr-2" />

                {t(
                  'auth.login.loginButton'
                )}
                ...
              </div>
            ) : (
              t(
                'auth.login.loginButton'
              )
            )}
          </button>
        </div>

        {/* REGISTER */}

        {!inline && (
          <div className="text-center">
            <p className="text-sm text-textSecondary">
              {t(
                'auth.login.noAccount'
              )}{' '}

              <Link
                to="/register"
                className="fo-link font-medium"
              >
                {t(
                  'auth.login.registerLink'
                )}
              </Link>
            </p>
          </div>
        )}
      </form>
    );

  /*
   * =========================================================
   * INLINE MODE
   * =========================================================
   */

  if (
    inline
  ) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          {onCancel && (
            <button
              type="button"
              onClick={
                onCancel
              }
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}

          <h2 className="text-2xl font-bold">
            {t(
              'auth.login.title'
            )}
          </h2>
        </div>

        {renderForm()}
      </div>
    );
  }

  /*
   * =========================================================
   * FULL PAGE MODE
   * =========================================================
   */

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* HEADER */}

        <div className="text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-300 to-accent-500 rounded-2xl mx-auto mb-6">
            <Lock className="w-8 h-8 text-white" />
          </div>

          <h2 className="text-3xl font-display font-bold text-textPrimary">
            {t(
              'auth.login.title'
            )}
          </h2>

          <p className="mt-2 text-sm text-textSecondary">
            {t(
              'auth.login.subtitle'
            )}
          </p>
        </div>

        {/* ACCOUNT CREATED */}

        {(location.state as any)
          ?.accountJustCreated && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />

              <div>
                <p className="text-sm font-semibold text-green-800">
                  {t(
                    'auth.login.accountCreatedTitle',
                    'Compte créé avec succès !'
                  )}
                </p>

                <p className="text-sm text-green-700 mt-1">
                  {t(
                    'auth.login.accountCreatedMessage',
                    {
                      defaultValue:
                        'Votre compte a été créé. Vérifiez votre adresse email avant de vous connecter.',
                      name:
                        (
                          location.state as any
                        )
                          ?.bookingUserName ||
                        '',
                    }
                  )}
                </p>

                <p className="text-xs text-green-600 mt-2">
                  {t(
                    'auth.login.verifyEmailHint',
                    'Un email de vérification a été envoyé à votre adresse email.'
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {renderForm()}

        {/* BACK HOME */}

        <div className="text-center">
          <Link
            to="/"
            className="text-sm text-textSecondary hover:text-textPrimary transition-colors inline-flex items-center justify-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />

            {t(
              'payment.cancelled.backHome',
              "Retour à l'accueil"
            )}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;